/**
 * The study framework's own words, and how a reader is shown them.
 *
 * Verbatim vocabularies, because they are the framework's rather than ours — status is
 * maturity, claim_type is epistemic kind, and origin records whose reading it was. None of
 * the three is inferred from any of the others, which is the whole reason they are three
 * lists and not one.
 *
 * Its own module so that a picker and a filter read the same list: `findingFields` offers
 * these as choices and `findingsList` offers them as filters, and a second copy in either
 * would let the interface accept a value the other cannot show.
 */

import type { Account } from '../account'
import { t } from '../i18n'

export const STATUSES = [
  'hypothesis',
  'developing',
  'established',
  'firm',
  'contested',
  'retired',
] as const
export const CLAIM_TYPES = ['asserted', 'synthesized', 'open'] as const
export const THREAD_STATUSES = ['open', 'deferred', 'resolved'] as const
export const ORIGINS = ['own', 'reused', 'mixed'] as const

/**
 * How to say where a claim came from.
 *
 * The stored value is an author's *name*; "own" is a relation, not a value,
 * and it holds only between a finding and the person reading it. So the label
 * is decided here, against the account — which is also why a pack carrying
 * somebody else's findings shows their name rather than silently promoting
 * them to the reader's own work.
 */
export function originLabel(origin: string | null, account: Account | null): string {
  if (!origin) return ''
  if (origin === 'reused' || origin === 'mixed') return t(`origin.${origin}`)
  const mine = [account?.display_name, account?.author_slug, 'own'].filter(Boolean)
  if (mine.includes(origin)) return t('origin.own')
  return t('origin.byAuthor', { author: origin })
}

/** The stored origin as one of the picker's three choices, or blank. */
export function asPickable(origin: string | null, account: Account | null): string {
  if (!origin) return ''
  if (origin === 'reused' || origin === 'mixed') return origin
  const mine = [account?.display_name, account?.author_slug, 'own'].filter(Boolean)
  return mine.includes(origin) ? 'own' : ''
}
